//src/pages/ResetPassword.tsx
import React, { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function ResetPassword() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event which fires when Supabase
    // exchanges the recovery token from the URL hash into a session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Session is now active — user can set a new password
        setError('');
      }
    });

    // Also check if a session already exists (e.g. page refresh)
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        // Give onAuthStateChange a moment to fire before showing the error
        setTimeout(async () => {
          const { data: retry } = await supabase.auth.getSession();
          if (!retry.session) {
            setError('Invalid or expired recovery link. Please request a new one.');
            setTimeout(() => navigate('/'), 3000);
          }
        }, 1000);
      }
    };

    checkSession();

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) throw error;

      setSuccess('Password updated successfully!');
      // Do NOT auto-redirect — let the browser's "save password" dialog
      // appear and the user interact with it before navigating away.
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white to-blue-50 p-6">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-6 h-6 text-[#236caf]" />
            <h2 className="text-2xl font-bold text-[#004060]">Reset Password</h2>
          </div>

          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">{error}</div>}
          {success && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm mb-4">{success}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent pr-16"
                  required
                  minLength={6}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 underline"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent pr-16"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 underline"
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading || !!success}>
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </Button>
          </form>

          {success && (
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-600 mb-4">
                Your browser may be prompting you to save your new password. Save it before continuing.
              </p>
              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/');
                }}
                className="w-full px-4 py-2 bg-[#236caf] text-white rounded-lg font-medium hover:bg-[#004060] transition-colors"
              >
                Continue to Login
              </button>
            </div>
          )}
        </div>
      </div>
  );
}
